// src/services/apiClient.ts
import { config } from '../config';
import { AppEvents } from '../constants/appEvents';
import { clearAuthStorage, getAccessToken } from '../utils/authStorage';

interface ApiClientOptions extends Omit<RequestInit, 'body'> {
    body?: unknown;
    skipAuth?: boolean;
    keepalive?: boolean;
    showLoading?: boolean;
}

export interface ApiValidationErrorItem {
    field?: string;
    message?: string;
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
    typeof v === 'object' && v !== null;

/** Extract message from API error envelope: { error: { message } } */
const getErrorMessage = (payload: unknown, fallback: string): string => {
    if (!isRecord(payload)) return fallback;

    const error = payload.error;
    if (isRecord(error) && typeof error.message === 'string') {
        return error.message;
    }

    if (typeof payload.message === 'string') return payload.message;
    if (typeof payload.detail === 'string') return payload.detail;

    return fallback;
};

/** Extract error.type as the error code */
const getErrorCode = (payload: unknown): string | undefined => {
    if (!isRecord(payload)) return undefined;
    const error = payload.error;
    if (!isRecord(error)) return undefined;
    return typeof error.type === 'string' ? error.type : undefined;
};

/** Extract structured validation errors from error.context.errors */
const getValidationErrors = (payload: unknown): ApiValidationErrorItem[] | undefined => {
    if (!isRecord(payload)) return undefined;
    const error = payload.error;
    if (!isRecord(error)) return undefined;
    const context = error.context;
    if (!isRecord(context)) return undefined;
    const errors = context.errors;
    if (!Array.isArray(errors)) return undefined;

    const normalized = errors
        .filter((item): item is Record<string, unknown> => isRecord(item))
        .map((item) => ({
            field: typeof item.field === 'string' ? item.field : undefined,
            message: typeof item.message === 'string' ? item.message : undefined,
        }))
        .filter((item) => item.message !== undefined || item.field !== undefined);

    return normalized.length > 0 ? normalized : undefined;
};

class ApiError extends Error {
    status: number;
    code?: string;
    payload?: unknown;
    validationErrors?: ApiValidationErrorItem[];

    constructor(message: string, status: number, code?: string, payload?: unknown, validationErrors?: ApiValidationErrorItem[]) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.code = code;
        this.payload = payload;
        this.validationErrors = validationErrors;
    }
}

const apiClient = async <T>(
    endpoint: string,
    options: ApiClientOptions = {}
): Promise<T> => {
    const { body, skipAuth = false, keepalive = false, showLoading = true, headers: customHeaders, ...restOptions } = options;

    if (showLoading) {
        window.dispatchEvent(new CustomEvent(AppEvents.apiLoadingStart));
    }

    try {
        const token = getAccessToken();

        const headers: Record<string, string> = {
            ...(customHeaders as Record<string, string>),
        };

        // Add auth header if not skipped and token exists
        if (!skipAuth && token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        // Add Content-Type for JSON bodies
        if (body && !(body instanceof FormData)) {
            headers['Content-Type'] = 'application/json';
        }

        const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
        const url = endpoint.startsWith('http') ? endpoint : `${config.apiUrl}${normalizedEndpoint}`;

        const response = await fetch(url, {
            ...restOptions,
            headers,
            keepalive,
            body: body instanceof FormData ? body : body ? JSON.stringify(body) : undefined,
        });

        // Handle 401 Unauthorized - redirect to login.
        // Skip for public endpoints (login, signup, etc.) where 401 means
        // "bad credentials", not "session expired".
        if (response.status === 401 && !skipAuth) {
            clearAuthStorage();

            // Dispatch custom event for auth context to handle
            window.dispatchEvent(new CustomEvent(AppEvents.authUnauthorized));

            throw new ApiError('Session expired. Please sign in again.', 401);
        }

        // Handle 204 No Content
        if (response.status === 204) {
            return undefined as unknown as T;
        }

        // Handle other errors
        if (!response.ok) {
            const fallbackMessage = `Request failed with status ${response.status}`;
            let errorMessage = fallbackMessage;
            let code: string | undefined;
            let payload: unknown;
            let validationErrors: ApiValidationErrorItem[] | undefined;
            try {
                const errorData = await response.json();
                payload = errorData;
                errorMessage = getErrorMessage(errorData, fallbackMessage);
                code = getErrorCode(errorData);
                validationErrors = getValidationErrors(errorData);
            } catch {
                // Ignore JSON parse errors
            }
            throw new ApiError(errorMessage, response.status, code, payload, validationErrors);
        }

        // Parse JSON response
        try {
            return await response.json();
        } catch {
            return undefined as unknown as T;
        }
    } finally {
        if (showLoading) {
            window.dispatchEvent(new CustomEvent(AppEvents.apiLoadingStop));
        }
    }
};

export { apiClient, ApiError };
export default apiClient;
